import React, { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLocation } from '../contexts/LocationContext';
import { usePermissions } from '../contexts/PermissionsContext';

// ── Style helpers ──────────────────────────────────────────────

const modalOverlay = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 1000
};

const modalBox = (maxW = '600px') => ({
  background: 'var(--card-bg)', padding: '24px', borderRadius: '12px',
  maxWidth: maxW, width: '90%', maxHeight: '90vh', overflow: 'auto'
});

const modalBoxClass = 'bonus-modal-box';

const card = {
  backgroundColor: 'var(--card-bg)', borderRadius: '4px',
  padding: '0.75rem', boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  marginBottom: '0.75rem'
};

const statCard = (color) => ({
  backgroundColor: 'var(--card-bg)', borderLeft: `3px solid ${color}`,
  borderRadius: '4px', padding: '0.5rem 0.75rem',
  textAlign: 'center', minWidth: '120px',
  boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
});

const tableStyle = {
  width: '100%', borderCollapse: 'collapse', fontSize: '12px'
};

const th = {
  textAlign: 'left', padding: '6px 8px', borderBottom: '2px solid var(--border)',
  color: 'var(--text-muted)', fontWeight: 600, fontSize: '11px', whiteSpace: 'nowrap'
};

const td = {
  padding: '6px 8px', borderBottom: '1px solid var(--border)', verticalAlign: 'middle'
};

const inputStyle = {
  width: '100%', padding: '6px 8px', borderRadius: '4px',
  border: '1px solid var(--border)', background: 'var(--bg)',
  color: 'var(--text)', fontSize: '12px'
};

const selectStyle = { ...inputStyle };

const sectionTitle = {
  fontSize: '13px', fontWeight: 600, color: 'var(--text)',
  marginBottom: '8px', marginTop: '16px'
};

const statusColors = {
  obliczona: 'var(--info)',
  zatwierdzona: 'var(--success)',
  wyplacona: 'var(--purple)',
  odrzucona: 'var(--danger)'
};

const statusLabels = {
  obliczona: 'Obliczona',
  zatwierdzona: 'Zatwierdzona',
  wyplacona: 'Wypłacona',
  odrzucona: 'Odrzucona'
};

const fmt = (v) => Number(v || 0).toFixed(2);

const progressColor = (pct) => {
  if (pct >= 80) return 'var(--success)';
  if (pct >= 50) return 'var(--warning)';
  return 'var(--danger)';
};

const dropdownStyle = {
  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 100,
  background: 'var(--card-bg)', border: '1px solid var(--border)',
  borderRadius: '4px', maxHeight: '200px', overflowY: 'auto',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
};

const dropdownItemStyle = {
  padding: '6px 10px', cursor: 'pointer', fontSize: '12px',
  borderBottom: '1px solid var(--border)'
};

// ── Badge helpers ──────────────────────────────────────────────

const StatusBadge = ({ status }) => {
  const bg = statusColors[status] || 'var(--border)';
  const label = statusLabels[status] || status;
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
      fontSize: '10px', fontWeight: 600, color: '#fff', backgroundColor: bg
    }}>
      {label}
    </span>
  );
};

const AlertBadge = ({ type, text }) => {
  if (!type) return null;
  const bg = type === 'danger' ? 'var(--danger)' : 'var(--warning)';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: '10px',
      fontSize: '10px', fontWeight: 600, color: '#fff', backgroundColor: bg
    }}>
      {text || type}
    </span>
  );
};

// ── Progress bar ───────────────────────────────────────────────

const ProgressBar = ({ value, max, height = 8, showLabel = false }) => {
  const pct = max > 0 ? Math.min(100, (value / max) * 100) : 0;
  return (
    <div style={{ width: '100%' }}>
      <div style={{
        width: '100%', height, borderRadius: height / 2,
        backgroundColor: 'var(--border)', overflow: 'hidden'
      }}>
        <div style={{
          width: `${pct}%`, height: '100%', borderRadius: height / 2,
          backgroundColor: progressColor(pct), transition: 'width 0.3s'
        }} />
      </div>
      {showLabel && (
        <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginTop: '2px', textAlign: 'right' }}>
          {fmt(value)} / {fmt(max)} ({pct.toFixed(0)}%)
        </div>
      )}
    </div>
  );
};

// ── Month selector helper ──────────────────────────────────────

const monthNames = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const MonthSelector = ({ rok, miesiac, onChange }) => {
  const options = [];
  const now = new Date();
  for (let y = now.getFullYear(); y >= now.getFullYear() - 1; y--) {
    for (let m = 12; m >= 1; m--) {
      options.push({ rok: y, miesiac: m, label: `${monthNames[m - 1]} ${y}` });
    }
  }
  return (
    <select
      style={{ ...selectStyle, width: 'auto', minWidth: '160px' }}
      value={`${rok}-${miesiac}`}
      onChange={(e) => {
        const [r, m] = e.target.value.split('-').map(Number);
        onChange(r, m);
      }}
    >
      {options.map((o) => (
        <option key={`${o.rok}-${o.miesiac}`} value={`${o.rok}-${o.miesiac}`}>{o.label}</option>
      ))}
    </select>
  );
};

// ── Product search autocomplete ────────────────────────────────

const ProductSearchInput = ({ onSelect }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [searching, setSearching] = useState(false);
  const timerRef = useRef(null);
  const wrapperRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSearch = (val) => {
    setQuery(val);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (val.trim().length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await api.get('/products/search', { params: { query: val, limit: 10 } });
        const data = res.data;
        let products = [];
        if (data.data && data.data.products) {
          products = data.data.products;
        } else if (data.data && Array.isArray(data.data)) {
          products = data.data;
        } else if (data.products && Array.isArray(data.products)) {
          products = data.products;
        }
        setResults(products);
        setShowDropdown(products.length > 0);
      } catch (err) {
        console.error('Product search error:', err);
        setResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);
  };

  const handleSelect = (product) => {
    onSelect(product);
    setQuery('');
    setResults([]);
    setShowDropdown(false);
  };

  return (
    <div ref={wrapperRef} style={{ position: 'relative' }}>
      <input
        type="text"
        style={inputStyle}
        placeholder="Wpisz nazwę, EAN lub kod produktu..."
        value={query}
        onChange={(e) => handleSearch(e.target.value)}
        onFocus={() => { if (results.length > 0) setShowDropdown(true); }}
        autoFocus
      />
      {searching && (
        <div style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', fontSize: '10px', color: 'var(--text-muted)' }}>
          Szukam...
        </div>
      )}
      {showDropdown && results.length > 0 && (
        <div style={dropdownStyle}>
          {results.map((p) => (
            <div
              key={p.id}
              style={dropdownItemStyle}
              onClick={() => handleSelect(p)}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}
            >
              <div style={{ fontWeight: 500 }}>{p.nazwa || p.name}</div>
              <div style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'flex', gap: '8px' }}>
                {(p.kategoria || p.category) && <span>Kat: {p.kategoria || p.category}</span>}
                {(p.ean || p.barcode || p.kod_kreskowy) && <span>EAN: {p.ean || p.barcode || p.kod_kreskowy}</span>}
                {(p.cena_sprzedazy_brutto || p.price) != null && <span>Cena: {fmt(p.cena_sprzedazy_brutto || p.price)} zł</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {showDropdown && results.length === 0 && query.trim().length >= 2 && !searching && (
        <div style={dropdownStyle}>
          <div style={{ ...dropdownItemStyle, color: 'var(--text-muted)', cursor: 'default' }}>
            Brak wyników
          </div>
        </div>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ════════════════════════════════════════════════════════════════

export default function BonusPage({ embedded = false }) {
  const { user } = useAuth();
  const { locationId, selectedLocation } = useLocation();
  const { hasPermission } = usePermissions();

  // Admin protection: check user type
  const isAdmin = hasPermission('premie', 'konfiguracja', locationId)
    || user?.typ === 'admin'
    || user?.user_type === 'admin';

  const now = new Date();
  const [rok, setRok] = useState(now.getFullYear());
  const [miesiac, setMiesiac] = useState(now.getMonth() + 1);

  const handleMonthChange = useCallback((r, m) => { setRok(r); setMiesiac(m); }, []);

  if (isAdmin) {
    return <AdminView rok={rok} miesiac={miesiac} onMonthChange={handleMonthChange} locationId={locationId} selectedLocation={selectedLocation} embedded={embedded} />;
  }

  return <EmployeeView rok={rok} miesiac={miesiac} onMonthChange={handleMonthChange} locationId={locationId} />;
}

// ════════════════════════════════════════════════════════════════
// ADMIN VIEW
// ════════════════════════════════════════════════════════════════

function AdminView({ rok, miesiac, onMonthChange, locationId, selectedLocation, embedded = false }) {
  const [activeTab, setActiveTab] = useState('config');

  const tabs = [
    { id: 'config', label: 'Konfiguracja' },
    { id: 'challenges', label: 'Wyzwania' },
    { id: 'report', label: 'Raport premii' }
  ];

  return (
    <div className="bonus-page-root" style={{ padding: embedded ? '0' : '0.75rem', fontSize: '12px' }}>
      <style>{`
        @media (max-width: 600px) {
          .bonus-page-root .bonus-modal-box { width: 95vw !important; max-width: 95vw !important; padding: 16px !important; }
          .bonus-page-root .bonus-stat-grid > div { min-width: 0 !important; flex: 1 1 calc(50% - 8px) !important; }
          .bonus-page-root .bonus-tab-controls { flex-direction: column !important; align-items: stretch !important; }
          .bonus-page-root .bonus-tab-controls > div { margin-left: 0 !important; flex-wrap: wrap !important; }
          .bonus-page-root .bonus-tab-controls .btn { flex: 1 1 auto !important; }
          .bonus-page-root .admin-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .bonus-page-root .admin-table-wrap table { min-width: 600px; }
          .bonus-page-root .bonus-form-grid { grid-template-columns: 1fr !important; }
        }
        @media (max-width: 900px) {
          .bonus-page-root .admin-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
        }
      `}</style>
      {!embedded && (
        <h4 style={{ margin: '0 0 12px', fontSize: '16px', color: 'var(--text)' }}>
          System premiowy
        </h4>
      )}

      <div className="tab-bar">
        {tabs.map((t) => (
          <button
            key={t.id}
            className={`tab-item${activeTab === t.id ? ' active' : ''}`}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {activeTab === 'config' && (
        <ConfigTab rok={rok} miesiac={miesiac} onMonthChange={onMonthChange} locationId={locationId} />
      )}
      {activeTab === 'challenges' && (
        <ChallengesTab />
      )}
      {activeTab === 'report' && (
        <ReportTab rok={rok} miesiac={miesiac} onMonthChange={onMonthChange} locationId={locationId} selectedLocation={selectedLocation} />
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 1: KONFIGURACJA
// ════════════════════════════════════════════════════════════════

function ConfigTab({ rok, miesiac, onMonthChange, locationId }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);
  const [thresholdTemplates, setThresholdTemplates] = useState([]);
  const [productTemplates, setProductTemplates] = useState([]);
  const [selectedThresholdTemplate, setSelectedThresholdTemplate] = useState('');
  const [selectedProductTemplate, setSelectedProductTemplate] = useState('');
  const [productSearch, setProductSearch] = useState('');

  // Threshold rows
  const [thresholds, setThresholds] = useState([]);
  // Premium product rows
  const [premiumProducts, setPremiumProducts] = useState([]);
  // Employees
  const [employees, setEmployees] = useState([]);

  // All system users (for employee select dropdown)
  const [allUsers, setAllUsers] = useState([]);
  // All locations (for bulk add)
  const [allLocations, setAllLocations] = useState([]);

  // New employee modal
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [bulkAddMode, setBulkAddMode] = useState(false);
  const [selectedLocationId, setSelectedLocationId] = useState('');
  const [bulkSelectedUsers, setBulkSelectedUsers] = useState([]);

  // Template name modal
  const [showTemplateName, setShowTemplateName] = useState(false);
  const [templateNameType, setTemplateNameType] = useState('thresholds');
  const [templateName, setTemplateName] = useState('');

  // New premium product modal
  const [showAddProduct, setShowAddProduct] = useState(false);
  const [newProduct, setNewProduct] = useState({ product_id: null, nazwa: '', kwota_za_sztuke: '', min_marza_procent: '', min_ilosc: 1, aktywny: true, typ: 'produkt' });

  // Fetch all system users and locations on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/bonus/available-users');
        if (res.data.success) {
          const d = res.data.data;
          setAllUsers(d.users || d || []);
          setAllLocations(d.locations || []);
        }
      } catch (err) {
        console.error('Blad pobierania uzytkownikow:', err);
        // Fallback to /auth/users
        try {
          const res2 = await api.get('/auth/users');
          if (res2.data.success) setAllUsers(res2.data.data || []);
        } catch (e2) { /* ignore */ }
      }
    })();
  }, []);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bonus/config', { params: { rok, miesiac } });
      if (res.data.success) {
        const d = res.data.data;
        setConfig(d);
        // Mapuj pola backendu na frontend
        setThresholds((d.progi || []).map(t => ({
          ...t,
          od: t.min_sprzedaz ?? t.od ?? 0,
          do: t.max_sprzedaz ?? t.do ?? null,
          premia_bazowa: t.premia_bazowa ?? 0
        })));
        // FIX: backend returns `produkty` NOT `produkty_premiowe`
        setPremiumProducts((d.produkty || d.produkty_premiowe || []).map(p => ({
          ...p,
          kwota_za_sztuke: p.kwota_za_sztuke ?? p.kwota_za_szt ?? 0,
          min_marza_procent: p.min_marza_procent ?? p.min_marza ?? 0,
          min_ilosc: p.min_ilosc ?? 1
        })));
        setEmployees((d.pracownicy || []).map(p => ({
          ...p,
          login: p.login || p.kasjer_login
        })));
      }
    } catch (err) {
      toast.error('Blad wczytywania konfiguracji');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [rok, miesiac]);

  const fetchTemplates = useCallback(async () => {
    try {
      const [tRes, pRes] = await Promise.all([
        api.get('/bonus/templates/thresholds'),
        api.get('/bonus/templates/products')
      ]);
      if (tRes.data.success) setThresholdTemplates(tRes.data.data || []);
      if (pRes.data.success) setProductTemplates(pRes.data.data || []);
    } catch (err) {
      console.error('Blad wczytywania szablonow:', err);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      // Mapuj nazwy pol frontendu na backend
      const progiMapped = thresholds.map(t => ({
        location_id: t.location_id || null,
        min_sprzedaz: t.od ?? t.min_sprzedaz ?? 0,
        max_sprzedaz: (t.do === null || t.do === '' || t.do === undefined) ? null : (t.max_sprzedaz === null ? null : (t.do ?? t.max_sprzedaz ?? null)),
        premia_bazowa: t.premia_bazowa ?? 0
      }));
      // FIX: send produkty with correct field names (kwota_za_sztuke, min_marza_procent)
      const produktyMapped = premiumProducts.map(p => ({
        ...p,
        kwota_za_sztuke: p.kwota_za_sztuke ?? p.kwota_za_szt ?? 0,
        min_marza_procent: p.min_marza_procent ?? p.min_marza ?? 0,
        min_ilosc: p.min_ilosc ?? 1
      }));
      const res = await api.put('/bonus/config', {
        rok, miesiac, progi: progiMapped,
        produkty: produktyMapped, pracownicy: employees
      });
      if (res.data.success) {
        toast.success('Konfiguracja zapisana');
      } else {
        toast.error(res.data.message || 'Blad zapisu');
      }
    } catch (err) {
      toast.error('Blad zapisu konfiguracji');
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleApplyTemplate = async (type, templateId) => {
    if (!templateId) return;
    try {
      const res = await api.post('/bonus/config/apply-template', {
        rok, miesiac, typ: type, template_id: templateId
      });
      if (res.data.success) {
        toast.success('Szablon zastosowany');
        fetchConfig();
      }
    } catch (err) {
      toast.error('Blad stosowania szablonu');
    }
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) return;
    try {
      const endpoint = templateNameType === 'thresholds'
        ? '/bonus/templates/thresholds'
        : '/bonus/templates/products';
      const payload = templateNameType === 'thresholds'
        ? { nazwa: templateName, dane: thresholds }
        : { nazwa: templateName, dane: premiumProducts };
      const res = await api.post(endpoint, payload);
      if (res.data.success) {
        toast.success('Szablon zapisany');
        fetchTemplates();
      }
    } catch (err) {
      toast.error('Blad zapisu szablonu');
    } finally {
      setShowTemplateName(false);
      setTemplateName('');
    }
  };

  const handleDeleteTemplate = async (type, templateId) => {
    if (!window.confirm('Na pewno usunac ten szablon?')) return;
    try {
      const endpoint = type === 'thresholds'
        ? `/bonus/templates/thresholds/${templateId}`
        : `/bonus/templates/products/${templateId}`;
      const res = await api.delete(endpoint);
      if (res.data.success) {
        toast.success('Szablon usuniety');
        if (type === 'thresholds') setSelectedThresholdTemplate('');
        else setSelectedProductTemplate('');
        fetchTemplates();
      }
    } catch (err) {
      console.error('Delete template error:', err);
      toast.error('Blad usuwania szablonu: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddEmployee = async () => {
    // Bulk mode: add multiple users at once
    if (bulkAddMode && bulkSelectedUsers.length > 0) {
      let added = 0, skipped = 0;
      for (const userId of bulkSelectedUsers) {
        const user = allUsers.find(u => String(u.id) === String(userId));
        if (!user) continue;
        if (employees.some(e => e.login === user.login)) { skipped++; continue; }
        try {
          const res = await api.post('/bonus/employees', {
            kasjer_login: user.login,
            lokalizacje: selectedLocationId ? [{ location_id: parseInt(selectedLocationId), deklarowane_dni: 22 }] : [],
            aktywny: true
          });
          if (res.data.success) added++;
        } catch (err) { skipped++; }
      }
      toast.success(`Dodano ${added} pracownikow` + (skipped ? `, pominieto ${skipped}` : ''));
      setShowAddEmployee(false);
      setBulkSelectedUsers([]);
      setSelectedLocationId('');
      setBulkAddMode(false);
      fetchConfig();
      return;
    }

    // Single mode
    if (!selectedUserId) return;
    const selectedUser = allUsers.find(u => String(u.id) === String(selectedUserId));
    if (!selectedUser) return;
    if (employees.some(e => e.login === selectedUser.login)) {
      toast.error('Ten pracownik jest juz w systemie premiowym');
      return;
    }
    const newEmp = {
      kasjer_login: selectedUser.login,
      lokalizacje: selectedLocationId ? [{ location_id: parseInt(selectedLocationId), deklarowane_dni: 22 }] : [],
      aktywny: true
    };
    try {
      const res = await api.post('/bonus/employees', newEmp);
      if (res.data.success) {
        toast.success('Pracownik dodany');
        setShowAddEmployee(false);
        setSelectedUserId('');
        setSelectedLocationId('');
        fetchConfig();
      }
    } catch (err) {
      toast.error('Blad dodawania pracownika');
    }
  };

  const handleAddAllFromLocation = () => {
    if (!selectedLocationId) return;
    // Select all users not yet in employees
    const available = allUsers.filter(u => !employees.some(e => e.login === u.login));
    setBulkSelectedUsers(available.map(u => String(u.id)));
  };

  // ── Threshold row helpers ──

  const addThresholdRow = () => {
    setThresholds([...thresholds, { od: 0, do: null, premia_bazowa: 0 }]);
  };

  const updateThreshold = (idx, field, value) => {
    const copy = [...thresholds];
    if (field === 'do' && (value === '' || value === null || value === undefined)) {
      copy[idx] = { ...copy[idx], [field]: null };
    } else {
      copy[idx] = { ...copy[idx], [field]: parseFloat(value) || 0 };
    }
    setThresholds(copy);
  };

  const removeThreshold = (idx) => {
    setThresholds(thresholds.filter((_, i) => i !== idx));
  };

  // ── Premium product helpers ──

  const addPremiumProduct = () => {
    if (!newProduct.nazwa.trim()) return;
    setPremiumProducts([...premiumProducts, {
      ...newProduct,
      kwota_za_sztuke: parseFloat(newProduct.kwota_za_sztuke) || 0,
      min_marza_procent: parseFloat(newProduct.min_marza_procent) || 0,
      min_ilosc: parseInt(newProduct.min_ilosc) || 1
    }]);
    setNewProduct({ product_id: null, nazwa: '', kwota_za_sztuke: '', min_marza_procent: '', min_ilosc: 1, aktywny: true, typ: 'produkt' });
    setShowAddProduct(false);
  };

  const handleProductSelected = (product) => {
    setNewProduct({
      ...newProduct,
      product_id: product.id,
      nazwa: product.nazwa || product.name || '',
      typ: 'produkt'
    });
  };

  const updatePremiumProduct = (idx, field, value) => {
    const copy = [...premiumProducts];
    copy[idx] = { ...copy[idx], [field]: value };
    setPremiumProducts(copy);
  };

  const removePremiumProduct = (idx) => {
    setPremiumProducts(premiumProducts.filter((_, i) => i !== idx));
  };

  // ── Filter premium products ──
  const filteredProducts = premiumProducts.filter((p) =>
    !productSearch || (p.nazwa || '').toLowerCase().includes(productSearch.toLowerCase())
  );

  if (loading) {
    return <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ladowanie konfiguracji...</div>;
  }

  return (
    <div>
      {/* Month selector + info */}
      <div style={{ ...card, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <MonthSelector rok={rok} miesiac={miesiac} onChange={onMonthChange} />
        <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
          Brak konfiguracji? Zostanie automatycznie skopiowana z poprzedniego miesiaca.
        </span>
      </div>

      {/* ── Progi sprzedazowe ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <h5 style={sectionTitle}>Progi sprzedazowe</h5>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <select style={{ ...selectStyle, width: 'auto', minWidth: '140px' }} value={selectedThresholdTemplate}
              onChange={(e) => { setSelectedThresholdTemplate(e.target.value); handleApplyTemplate('thresholds', e.target.value); }}>
              <option value="">-- Szablon --</option>
              {thresholdTemplates.map((t) => <option key={t.id} value={t.id}>{t.nazwa}</option>)}
            </select>
            {selectedThresholdTemplate && (
              <button className="btn" style={{ fontSize: '11px', padding: '4px 10px', color: 'var(--danger)' }}
                onClick={() => handleDeleteTemplate('thresholds', selectedThresholdTemplate)} title="Usun szablon">
                🗑
              </button>
            )}
            <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 10px' }}
              onClick={() => { setTemplateNameType('thresholds'); setShowTemplateName(true); }}>
              Zapisz jako szablon
            </button>
          </div>
        </div>
        <div className="admin-table-wrap">
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Od (zl)</th>
              <th style={th}>Do (zl)</th>
              <th style={th}>Premia bazowa (zl)</th>
              <th style={{ ...th, width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {thresholds.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>Brak progow. Dodaj pierwszy prog.</td></tr>
            )}
            {thresholds.map((row, idx) => (
              <tr key={idx}>
                <td style={td}>
                  <input type="number" style={{ ...inputStyle, width: '100px' }} value={row.od}
                    onChange={(e) => updateThreshold(idx, 'od', e.target.value)} />
                </td>
                <td style={td}>
                  <input type="number" style={{ ...inputStyle, width: '100px' }} value={row.do ?? ''}
                    placeholder="bez limitu"
                    onChange={(e) => updateThreshold(idx, 'do', e.target.value)} />
                </td>
                <td style={td}>
                  <input type="number" step="0.01" style={{ ...inputStyle, width: '100px' }} value={row.premia_bazowa}
                    onChange={(e) => updateThreshold(idx, 'premia_bazowa', e.target.value)} />
                </td>
                <td style={td}>
                  <button style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '14px' }}
                    onClick={() => removeThreshold(idx)} title="Usun">✕</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <button className="btn btn-primary" style={{ fontSize: '11px', marginTop: '8px', padding: '4px 10px' }}
          onClick={addThresholdRow}>
          + Dodaj prog
        </button>
      </div>

      {/* ── Produkty premiowe ── */}
      <div style={card}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
          <h5 style={sectionTitle}>Produkty premiowe</h5>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <select style={{ ...selectStyle, width: 'auto', minWidth: '140px' }} value={selectedProductTemplate}
              onChange={(e) => { setSelectedProductTemplate(e.target.value); handleApplyTemplate('products', e.target.value); }}>
              <option value="">-- Szablon --</option>
              {productTemplates.map((t) => <option key={t.id} value={t.id}>{t.nazwa}</option>)}
            </select>
            {selectedProductTemplate && (
              <button className="btn" style={{ fontSize: '11px', padding: '4px 10px', color: 'var(--danger)' }}
                onClick={() => handleDeleteTemplate('products', selectedProductTemplate)} title="Usun szablon">
                🗑
              </button>
            )}
            <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 10px' }}
              onClick={() => { setTemplateNameType('products'); setShowTemplateName(true); }}>
              Zapisz jako szablon
            </button>
          </div>
        </div>

        <input type="text" placeholder="Filtruj produkty / kategorie..." style={{ ...inputStyle, marginBottom: '8px', maxWidth: '300px' }}
          value={productSearch} onChange={(e) => setProductSearch(e.target.value)} />

        <div className="admin-table-wrap">
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Produkt / Kategoria</th>
              <th style={th}>Kwota za szt. (zl)</th>
              <th style={th}>Min marza %</th>
              <th style={th}>Min ilosc</th>
              <th style={th}>Aktywny</th>
              <th style={{ ...th, width: '40px' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredProducts.length === 0 && (
              <tr><td colSpan={6} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>
                {productSearch ? 'Brak wynikow' : 'Brak produktow premiowych. Dodaj pierwszy.'}
              </td></tr>
            )}
            {filteredProducts.map((p, idx) => {
              const realIdx = premiumProducts.indexOf(p);
              return (
                <tr key={realIdx}>
                  <td style={td}>
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', marginRight: '4px' }}>
                      {p.typ === 'kategoria' ? '[kat]' : '[prod]'}
                    </span>
                    {p.nazwa}
                  </td>
                  <td style={td}>
                    <input type="number" step="0.01" style={{ ...inputStyle, width: '80px' }}
                      value={p.kwota_za_sztuke ?? p.kwota_za_szt ?? 0}
                      onChange={(e) => updatePremiumProduct(realIdx, 'kwota_za_sztuke', parseFloat(e.target.value) || 0)} />
                  </td>
                  <td style={td}>
                    <input type="number" step="0.1" style={{ ...inputStyle, width: '70px' }}
                      value={p.min_marza_procent ?? p.min_marza ?? 0}
                      onChange={(e) => updatePremiumProduct(realIdx, 'min_marza_procent', parseFloat(e.target.value) || 0)} />
                  </td>
                  <td style={td}>
                    <input type="number" step="1" min="1" style={{ ...inputStyle, width: '60px' }}
                      value={p.min_ilosc ?? 1}
                      onChange={(e) => updatePremiumProduct(realIdx, 'min_ilosc', parseInt(e.target.value) || 1)} />
                  </td>
                  <td style={td}>
                    <input type="checkbox" checked={!!p.aktywny}
                      onChange={(e) => updatePremiumProduct(realIdx, 'aktywny', e.target.checked)} />
                  </td>
                  <td style={td}>
                    <button style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: '14px' }}
                      onClick={() => removePremiumProduct(realIdx)} title="Usun">✕</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
        <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 10px' }}
            onClick={() => { setNewProduct({ product_id: null, nazwa: '', kwota_za_sztuke: '', min_marza_procent: '', min_ilosc: 1, aktywny: true, typ: 'produkt' }); setShowAddProduct(true); }}>
            + Dodaj produkt
          </button>
          <button className="btn btn-primary" style={{ fontSize: '11px', padding: '4px 10px' }}
            onClick={() => { setNewProduct({ product_id: null, nazwa: '', kwota_za_sztuke: '', min_marza_procent: '', min_ilosc: 1, aktywny: true, typ: 'kategoria' }); setShowAddProduct(true); }}>
            + Dodaj kategorie
          </button>
        </div>
      </div>

      {/* ── Pracownicy ── */}
      <div style={card}>
        <h5 style={sectionTitle}>Pracownicy</h5>
        <div className="admin-table-wrap">
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={th}>Login</th>
              <th style={th}>Lokalizacje domyslne</th>
              <th style={th}>Deklarowane dni</th>
              <th style={th}>Aktywny</th>
            </tr>
          </thead>
          <tbody>
            {employees.length === 0 && (
              <tr><td colSpan={4} style={{ ...td, textAlign: 'center', color: 'var(--text-muted)' }}>Brak pracownikow w systemie premiowym.</td></tr>
            )}
            {employees.map((emp, idx) => (
              <tr key={idx}>
                <td style={td}>{emp.login}</td>
                <td style={td}>
                  <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                    {(emp.lokalizacje || []).map((loc, i) => {
                      const locName = allLocations.find(l => l.id === loc.location_id)?.nazwa || `Lok. ${loc.location_id}`;
                      return (
                        <span key={i} style={{
                          display: 'inline-block', padding: '1px 6px', borderRadius: '8px',
                          fontSize: '10px', backgroundColor: 'var(--border)', color: 'var(--text)'
                        }}>
                          {locName} ({loc.deklarowane_dni}d)
                        </span>
                      );
                    })}
                  </div>
                </td>
                <td style={td}>
                  <input type="number" style={{ ...inputStyle, width: '60px' }}
                    value={emp.deklarowane_dni || 0}
                    onChange={(e) => {
                      const copy = [...employees];
                      copy[idx] = { ...copy[idx], deklarowane_dni: parseInt(e.target.value) || 0 };
                      setEmployees(copy);
                    }} />
                </td>
                <td style={td}>
                  <input type="checkbox" checked={!!emp.aktywny}
                    onChange={(e) => {
                      const copy = [...employees];
                      copy[idx] = { ...copy[idx], aktywny: e.target.checked };
                      setEmployees(copy);
                    }} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <button className="btn btn-primary" style={{ fontSize: '11px', marginTop: '8px', padding: '4px 10px' }}
          onClick={() => { setSelectedUserId(''); setShowAddEmployee(true); }}>
          + Dodaj pracownika
        </button>
      </div>

      {/* ── Save button ── */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '8px' }}>
        <button className="btn btn-primary" style={{ padding: '6px 12px', fontWeight: 600 }}
          onClick={handleSaveConfig} disabled={saving}>
          {saving ? 'Zapisywanie...' : 'Zapisz konfiguracje'}
        </button>
      </div>

      {/* ── Modals ── */}

      {/* Template name modal */}
      {showTemplateName && (
        <div style={modalOverlay} onClick={() => setShowTemplateName(false)}>
          <div className={modalBoxClass} style={modalBox('400px')} onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginTop: 0, marginBottom: '12px' }}>Zapisz szablon</h5>
            <input type="text" placeholder="Nazwa szablonu" style={inputStyle}
              value={templateName} onChange={(e) => setTemplateName(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveTemplate()} autoFocus />
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
              <button className="btn" onClick={() => setShowTemplateName(false)}>Anuluj</button>
              <button className="btn btn-primary" onClick={handleSaveTemplate} disabled={!templateName.trim()}>Zapisz</button>
            </div>
          </div>
        </div>
      )}

      {/* Add employee modal — single or bulk mode */}
      {showAddEmployee && (
        <div style={modalOverlay} onClick={() => { setShowAddEmployee(false); setBulkAddMode(false); }}>
          <div className={modalBoxClass} style={modalBox('500px')} onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginTop: 0, marginBottom: '12px' }}>Dodaj pracownika</h5>

            {/* Mode toggle */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
              <button className={`btn ${!bulkAddMode ? 'btn-primary' : ''}`} style={{ fontSize: '11px', padding: '4px 10px' }}
                onClick={() => setBulkAddMode(false)}>
                Pojedynczy
              </button>
              <button className={`btn ${bulkAddMode ? 'btn-primary' : ''}`} style={{ fontSize: '11px', padding: '4px 10px' }}
                onClick={() => setBulkAddMode(true)}>
                Wielu / cala lokalizacja
              </button>
            </div>

            {/* Location selector (common for both modes) */}
            {allLocations.length > 0 && (
              <label style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block', marginBottom: '10px' }}>
                Lokalizacja domyslna
                <select style={selectStyle} value={selectedLocationId}
                  onChange={(e) => setSelectedLocationId(e.target.value)}>
                  <option value="">-- Bez lokalizacji --</option>
                  {allLocations.map(l => (
                    <option key={l.id} value={l.id}>{l.nazwa}</option>
                  ))}
                </select>
              </label>
            )}

            {!bulkAddMode ? (
              /* Single user mode */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Wybierz uzytkownika
                  <select style={selectStyle} value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)} autoFocus>
                    <option value="">-- Wybierz --</option>
                    {allUsers
                      .filter(u => !employees.some(e => e.login === u.login))
                      .map(u => (
                        <option key={u.id} value={u.id}>
                          {u.login} {u.typ ? `(${u.typ})` : ''}
                        </option>
                      ))
                    }
                  </select>
                </label>
              </div>
            ) : (
              /* Bulk mode - checkboxes */
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                    Zaznacz pracownikow ({bulkSelectedUsers.length} wybranych)
                  </span>
                  <div style={{ display: 'flex', gap: '6px' }}>
                    <button className="btn" style={{ fontSize: '11px', padding: '4px 10px' }}
                      onClick={() => {
                        const avail = allUsers.filter(u => !employees.some(e => e.login === u.login));
                        setBulkSelectedUsers(avail.map(u => String(u.id)));
                      }}>
                      Zaznacz wszystkich
                    </button>
                    <button className="btn" style={{ fontSize: '11px', padding: '4px 10px' }}
                      onClick={() => setBulkSelectedUsers([])}>
                      Odznacz
                    </button>
                  </div>
                </div>
                <div style={{ maxHeight: '200px', overflowY: 'auto', border: '1px solid var(--border)', borderRadius: '6px', padding: '6px' }}>
                  {allUsers
                    .filter(u => !employees.some(e => e.login === u.login))
                    .map(u => (
                      <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 6px', cursor: 'pointer', fontSize: '12px',
                        background: bulkSelectedUsers.includes(String(u.id)) ? 'var(--primary-light, #e8f5e9)' : 'transparent',
                        borderRadius: '4px', marginBottom: '2px' }}>
                        <input type="checkbox" checked={bulkSelectedUsers.includes(String(u.id))}
                          onChange={(e) => {
                            if (e.target.checked) setBulkSelectedUsers([...bulkSelectedUsers, String(u.id)]);
                            else setBulkSelectedUsers(bulkSelectedUsers.filter(id => id !== String(u.id)));
                          }} />
                        {u.login} {u.typ ? <span style={{ color: 'var(--text-muted)', fontSize: '10px' }}>({u.typ})</span> : ''}
                      </label>
                    ))
                  }
                  {allUsers.filter(u => !employees.some(e => e.login === u.login)).length === 0 && (
                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', textAlign: 'center', padding: '12px' }}>
                      Wszyscy uzytkownicy sa juz dodani
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn" onClick={() => { setShowAddEmployee(false); setBulkAddMode(false); }}>Anuluj</button>
              <button className="btn btn-primary" onClick={handleAddEmployee}
                disabled={bulkAddMode ? bulkSelectedUsers.length === 0 : !selectedUserId}>
                {bulkAddMode ? `Dodaj (${bulkSelectedUsers.length})` : 'Dodaj'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add product/category modal — now with product search autocomplete */}
      {showAddProduct && (
        <div style={modalOverlay} onClick={() => setShowAddProduct(false)}>
          <div className={modalBoxClass} style={modalBox('480px')} onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginTop: 0, marginBottom: '12px' }}>
              {newProduct.typ === 'kategoria' ? 'Dodaj kategorie premiowa' : 'Dodaj produkt premiowy'}
            </h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {newProduct.typ === 'produkt' ? (
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Wyszukaj produkt z bazy
                  <ProductSearchInput onSelect={handleProductSelected} />
                  {newProduct.product_id && (
                    <div style={{ marginTop: '4px', padding: '6px 8px', borderRadius: '4px', background: 'var(--bg)', fontSize: '11px' }}>
                      Wybrany: <strong>{newProduct.nazwa}</strong> (ID: {newProduct.product_id})
                    </div>
                  )}
                </label>
              ) : (
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Nazwa kategorii
                  <input type="text" style={inputStyle} value={newProduct.nazwa}
                    onChange={(e) => setNewProduct({ ...newProduct, nazwa: e.target.value })} autoFocus />
                </label>
              )}
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Kwota za szt. (zl)
                <input type="number" step="0.01" style={inputStyle} value={newProduct.kwota_za_sztuke}
                  onChange={(e) => setNewProduct({ ...newProduct, kwota_za_sztuke: e.target.value })} />
              </label>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Min marza %
                <input type="number" step="0.1" style={inputStyle} value={newProduct.min_marza_procent}
                  onChange={(e) => setNewProduct({ ...newProduct, min_marza_procent: e.target.value })} />
              </label>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Min ilosc sprzedanych szt.
                <input type="number" step="1" min="1" style={inputStyle} value={newProduct.min_ilosc}
                  onChange={(e) => setNewProduct({ ...newProduct, min_ilosc: e.target.value })} />
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', display: 'block', marginTop: '2px' }}>
                  Premia nalicza sie dopiero po sprzedaniu min. tej ilosci w miesiacu
                </span>
              </label>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn" onClick={() => setShowAddProduct(false)}>Anuluj</button>
              <button className="btn btn-primary" onClick={addPremiumProduct}
                disabled={!newProduct.nazwa.trim()}>
                Dodaj
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 2: WYZWANIA
// ════════════════════════════════════════════════════════════════

function ChallengesTab() {
  const [loading, setLoading] = useState(true);
  const [challenges, setChallenges] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  // FIX: use correct backend field names: typ_celu, cel_wartosc, nagroda, data_od, data_do
  const [form, setForm] = useState({ nazwa: '', opis: '', typ_celu: 'sprzedaz', cel_wartosc: '', nagroda: '', data_od: '', data_do: '' });

  const fetchChallenges = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bonus/challenges');
      if (res.data.success) {
        setChallenges(res.data.data || []);
      }
    } catch (err) {
      toast.error('Blad wczytywania wyzwan');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchChallenges(); }, [fetchChallenges]);

  const openNew = () => {
    setEditing(null);
    setForm({ nazwa: '', opis: '', typ_celu: 'sprzedaz', cel_wartosc: '', nagroda: '', data_od: '', data_do: '' });
    setShowModal(true);
  };

  const openEdit = (ch) => {
    setEditing(ch);
    setForm({
      nazwa: ch.nazwa || '', opis: ch.opis || '',
      typ_celu: ch.typ_celu || 'sprzedaz',
      cel_wartosc: ch.cel_wartosc ?? ch.cel ?? '',
      nagroda: ch.nagroda || '',
      data_od: ch.data_od || '', data_do: ch.data_do || ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    try {
      // FIX: send correct backend field names
      const payload = {
        nazwa: form.nazwa,
        opis: form.opis,
        typ_celu: form.typ_celu,
        cel_wartosc: parseFloat(form.cel_wartosc) || 0,
        nagroda: parseFloat(form.nagroda) || 0,
        data_od: form.data_od,
        data_do: form.data_do
      };
      if (editing) {
        const res = await api.put(`/bonus/challenges/${editing.id}`, payload);
        if (res.data.success) toast.success('Wyzwanie zaktualizowane');
      } else {
        const res = await api.post('/bonus/challenges', payload);
        if (res.data.success) toast.success('Wyzwanie utworzone');
      }
      setShowModal(false);
      fetchChallenges();
    } catch (err) {
      toast.error('Blad zapisu wyzwania');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno chcesz usunac to wyzwanie?')) return;
    try {
      const res = await api.delete(`/bonus/challenges/${id}`);
      if (res.data.success) {
        toast.success('Wyzwanie usuniete');
        fetchChallenges();
      }
    } catch (err) {
      toast.error('Blad usuwania wyzwania');
    }
  };

  if (loading) {
    return <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ladowanie wyzwan...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '8px' }}>
        <button className="btn btn-primary" onClick={openNew}>+ Nowe wyzwanie</button>
      </div>

      {challenges.length === 0 && (
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Brak aktywnych wyzwan. Utworz pierwsze wyzwanie.
        </div>
      )}

      {challenges.map((ch) => (
        <div key={ch.id} style={{ ...card, padding: '12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: '13px', color: 'var(--text)' }}>{ch.nazwa}</div>
              {ch.opis && <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px' }}>{ch.opis}</div>}
            </div>
            <div style={{ display: 'flex', gap: '6px' }}>
              <button className="btn" style={{ fontSize: '11px', padding: '4px 10px' }} onClick={() => openEdit(ch)}>Edytuj</button>
              <button className="btn" style={{ fontSize: '11px', padding: '4px 10px', color: 'var(--danger)' }}
                onClick={() => handleDelete(ch.id)}>Usun</button>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '8px' }}>
            {ch.typ_celu && <span>Typ: <strong style={{ color: 'var(--text)' }}>{ch.typ_celu}</strong></span>}
            {/* FIX: backend returns cel_wartosc not cel */}
            <span>Cel: <strong style={{ color: 'var(--text)' }}>{fmt(ch.cel_wartosc ?? ch.cel)}</strong></span>
            <span>Nagroda: <strong style={{ color: 'var(--success)' }}>{fmt(ch.nagroda)} zl</strong></span>
            {ch.data_od && <span>{ch.data_od} — {ch.data_do}</span>}
          </div>
          {/* Employee progress bars */}
          {(ch.postepy || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {ch.postepy.map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '11px', color: 'var(--text)', minWidth: '80px' }}>{p.pracownik}</span>
                  <div style={{ flex: 1 }}>
                    <ProgressBar value={p.wartosc || 0} max={(ch.cel_wartosc ?? ch.cel) || 1} height={6} />
                  </div>
                  <span style={{ fontSize: '10px', color: 'var(--text-muted)', minWidth: '50px', textAlign: 'right' }}>
                    {fmt(p.wartosc)} / {fmt(ch.cel_wartosc ?? ch.cel)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}

      {/* Challenge form modal */}
      {showModal && (
        <div style={modalOverlay} onClick={() => setShowModal(false)}>
          <div className={modalBoxClass} style={modalBox('500px')} onClick={(e) => e.stopPropagation()}>
            <h5 style={{ marginTop: 0, marginBottom: '12px' }}>
              {editing ? 'Edytuj wyzwanie' : 'Nowe wyzwanie'}
            </h5>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Nazwa
                <input type="text" style={inputStyle} value={form.nazwa}
                  onChange={(e) => setForm({ ...form, nazwa: e.target.value })} autoFocus />
              </label>
              <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                Opis
                <textarea style={{ ...inputStyle, minHeight: '60px', resize: 'vertical' }} value={form.opis}
                  onChange={(e) => setForm({ ...form, opis: e.target.value })} />
              </label>
              <div className="bonus-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Typ celu
                  <select style={selectStyle} value={form.typ_celu}
                    onChange={(e) => setForm({ ...form, typ_celu: e.target.value })}>
                    <option value="sprzedaz">Sprzedaz</option>
                    <option value="ilosc">Ilosc</option>
                    <option value="marza">Marza</option>
                    <option value="produkt">Produkt</option>
                  </select>
                </label>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Cel (wartosc)
                  <input type="number" step="0.01" style={inputStyle} value={form.cel_wartosc}
                    onChange={(e) => setForm({ ...form, cel_wartosc: e.target.value })} />
                </label>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Nagroda (zl)
                  <input type="number" step="0.01" style={inputStyle} value={form.nagroda}
                    onChange={(e) => setForm({ ...form, nagroda: e.target.value })} />
                </label>
                <div></div>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Data od
                  <input type="date" style={inputStyle} value={form.data_od}
                    onChange={(e) => setForm({ ...form, data_od: e.target.value })} />
                </label>
                <label style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                  Data do
                  <input type="date" style={inputStyle} value={form.data_do}
                    onChange={(e) => setForm({ ...form, data_do: e.target.value })} />
                </label>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button className="btn" onClick={() => setShowModal(false)}>Anuluj</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={!form.nazwa.trim()}>
                {editing ? 'Zapisz' : 'Utworz'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// TAB 3: RAPORT PREMII
// ════════════════════════════════════════════════════════════════

function ReportTab({ rok, miesiac, onMonthChange, locationId, selectedLocation }) {
  const [loading, setLoading] = useState(false);
  const [calculating, setCalculating] = useState(false);
  const [approving, setApproving] = useState(false);
  const [summary, setSummary] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [drillDown, setDrillDown] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/bonus/summary', { params: { rok, miesiac, location_id: locationId } });
      if (res.data.success) {
        setSummary(res.data.data || []);
      }
    } catch (err) {
      console.error('Blad wczytywania raportu:', err);
    } finally {
      setLoading(false);
    }
  }, [rok, miesiac, locationId]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const handleCalculate = async () => {
    setCalculating(true);
    try {
      const res = await api.post('/bonus/calculate', { rok, miesiac, location_id: locationId });
      if (res.data.success) {
        toast.success('Premie obliczone');
        fetchSummary();
      } else {
        toast.error(res.data.message || 'Blad obliczania');
      }
    } catch (err) {
      toast.error('Blad obliczania premii');
    } finally {
      setCalculating(false);
    }
  };

  const handleApproveAll = async () => {
    if (!window.confirm('Czy na pewno chcesz zatwierdzic wszystkie premie?')) return;
    setApproving(true);
    try {
      const res = await api.post('/bonus/approve-all', { rok, miesiac, location_id: locationId });
      if (res.data.success) {
        toast.success('Wszystkie premie zatwierdzone');
        fetchSummary();
      }
    } catch (err) {
      toast.error('Blad zatwierdzania');
    } finally {
      setApproving(false);
    }
  };

  const handleApproveOne = async (id) => {
    try {
      const res = await api.post(`/bonus/${id}/approve`);
      if (res.data.success) {
        toast.success('Premia zatwierdzona');
        fetchSummary();
      }
    } catch (err) {
      toast.error('Blad zatwierdzania');
    }
  };

  const handleRowClick = async (row) => {
    setSelectedRow(row);
    setDrillLoading(true);
    try {
      // Use preview endpoint with kasjer_login for admin drill-down
      const res = await api.get(`/bonus/preview/${encodeURIComponent(row.pracownik)}`, {
        params: { rok, miesiac, location_id: locationId }
      });
      if (res.data.success) {
        const data = res.data.data;
        // Map fields for display
        setDrillDown({
          sprzedaz: data.sprzedaz_ogolem || 0,
          premia_bazowa: data.premia_bazowa || 0,
          premia_produktowa: data.premia_produktowa || 0,
          razem: data.premia_razem || 0,
          produkty: (data.szczegoly || {}).produkty || [],
          wyzwania: data.wyzwania || []
        });
      }
    } catch (err) {
      toast.error('Blad wczytywania szczegulow');
    } finally {
      setDrillLoading(false);
    }
  };

  return (
    <div>
      {/* Controls */}
      <div className="bonus-tab-controls" style={{ ...card, display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <MonthSelector rok={rok} miesiac={miesiac} onChange={onMonthChange} />
        {selectedLocation && (
          <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Lokalizacja: {selectedLocation.nazwa || selectedLocation.name}</span>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button className="btn btn-primary" onClick={handleCalculate} disabled={calculating}>
            {calculating ? 'Obliczanie...' : 'Oblicz premie'}
          </button>
          <button className="btn btn-primary" onClick={handleApproveAll} disabled={approving || summary.length === 0}>
            {approving ? 'Zatwierdzanie...' : 'Zatwierdz wszystkie'}
          </button>
        </div>
      </div>

      {/* Summary table */}
      {loading ? (
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ladowanie raportu...</div>
      ) : summary.length === 0 ? (
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
          Brak danych. Oblicz premie za wybrany miesiac.
        </div>
      ) : (
        <div className="admin-table-wrap" style={{ ...card, overflowX: 'auto' }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Pracownik</th>
                <th style={th}>Lokalizacja</th>
                <th style={th}>Dni (fakt/dekl)</th>
                <th style={{ ...th, textAlign: 'right' }}>Sprzedaz</th>
                <th style={{ ...th, textAlign: 'right' }}>Prog efekt.</th>
                <th style={{ ...th, textAlign: 'right' }}>Bazowa</th>
                <th style={{ ...th, textAlign: 'right' }}>Produkt.</th>
                <th style={{ ...th, textAlign: 'right' }}>Wyzwania</th>
                <th style={{ ...th, textAlign: 'right', fontWeight: 700 }}>RAZEM</th>
                <th style={th}>Status</th>
                <th style={th}>Alert</th>
              </tr>
            </thead>
            <tbody>
              {summary.map((row) => (
                <tr key={row.id} style={{ cursor: 'pointer' }}
                  onClick={() => handleRowClick(row)}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = ''; }}>
                  <td style={{ ...td, fontWeight: 500 }}>{row.pracownik}</td>
                  <td style={td}>{row.lokalizacja}</td>
                  <td style={td}>{row.dni_faktyczne ?? '—'} / {row.dni_deklarowane ?? '—'}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(row.sprzedaz)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(row.prog_efektywny)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(row.premia_bazowa)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(row.premia_produktowa)}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(row.premia_wyzwania)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 700, color: 'var(--primary)' }}>{fmt(row.razem)}</td>
                  <td style={td}><StatusBadge status={row.status} /></td>
                  <td style={td}><AlertBadge type={row.alert_type} text={row.alert_text} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Drill-down modal */}
      {selectedRow && (
        <div style={modalOverlay} onClick={() => { setSelectedRow(null); setDrillDown(null); }}>
          <div className={modalBoxClass} style={modalBox('700px')} onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <h5 style={{ margin: 0 }}>Szczegoly premii — {selectedRow.pracownik}</h5>
              <button style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: '16px' }}
                onClick={() => { setSelectedRow(null); setDrillDown(null); }}>✕</button>
            </div>

            {drillLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ladowanie...</div>
            ) : drillDown ? (
              <div>
                {/* Summary cards */}
                <div className="bonus-stat-grid" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                  <div style={statCard('var(--info)')}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Sprzedaz</div>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{fmt(drillDown.sprzedaz)} zl</div>
                  </div>
                  <div style={statCard('var(--primary)')}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Bazowa</div>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{fmt(drillDown.premia_bazowa)} zl</div>
                  </div>
                  <div style={statCard('var(--warning)')}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Produktowa</div>
                    <div style={{ fontSize: '16px', fontWeight: 700 }}>{fmt(drillDown.premia_produktowa)} zl</div>
                  </div>
                  <div style={statCard('var(--success)')}>
                    <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>RAZEM</div>
                    <div style={{ fontSize: '16px', fontWeight: 700, color: 'var(--primary)' }}>{fmt(drillDown.razem)} zl</div>
                  </div>
                </div>

                {/* Product sales detail */}
                {(drillDown.produkty || []).length > 0 && (
                  <div style={{ marginBottom: '12px' }}>
                    <h6 style={{ fontSize: '12px', marginBottom: '6px' }}>Sprzedaz produktow premiowych</h6>
                    <div className="admin-table-wrap">
                    <table style={tableStyle}>
                      <thead>
                        <tr>
                          <th style={th}>Produkt</th>
                          <th style={{ ...th, textAlign: 'right' }}>Ilosc</th>
                          <th style={{ ...th, textAlign: 'right' }}>Kwota/szt.</th>
                          <th style={{ ...th, textAlign: 'right' }}>Premia</th>
                        </tr>
                      </thead>
                      <tbody>
                        {drillDown.produkty.map((p, i) => (
                          <tr key={i}>
                            <td style={td}>{p.nazwa}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{p.ilosc}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmt(p.kwota_za_sztuke ?? p.kwota_za_szt)}</td>
                            <td style={{ ...td, textAlign: 'right' }}>{fmt(p.premia)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    </div>
                  </div>
                )}

                {/* Challenges detail */}
                {(drillDown.wyzwania || []).length > 0 && (
                  <div>
                    <h6 style={{ fontSize: '12px', marginBottom: '6px' }}>Wyzwania</h6>
                    {drillDown.wyzwania.map((w, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', minWidth: '120px' }}>{w.nazwa}</span>
                        <div style={{ flex: 1 }}><ProgressBar value={w.postep || 0} max={(w.cel_wartosc ?? w.cel) || 1} height={6} /></div>
                        <span style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{fmt(w.nagroda)} zl</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Approve button if not yet approved */}
                {selectedRow.status === 'obliczona' && (
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '12px' }}>
                    <button className="btn btn-primary" onClick={() => { handleApproveOne(selectedRow.id); setSelectedRow(null); setDrillDown(null); }}>
                      Zatwierdz premie
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Brak danych szczegolowych.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// EMPLOYEE VIEW — "Moja premia"
// ════════════════════════════════════════════════════════════════

function EmployeeView({ rok, miesiac, onMonthChange, locationId }) {
  const [loading, setLoading] = useState(true);
  const [bonus, setBonus] = useState(null);
  const [details, setDetails] = useState(null);
  const [challenges, setChallenges] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { rok, miesiac };
      if (locationId) params.location_id = locationId;
      const [bonusRes, detailsRes, challengesRes] = await Promise.all([
        api.get('/bonus/my', { params: locationId ? { location_id: locationId } : {} }),
        api.get('/bonus/my/details', { params }),
        api.get('/bonus/challenges', { params: locationId ? { location_id: locationId } : {} })
      ]);
      if (bonusRes.data.success) setBonus(bonusRes.data.data);
      if (detailsRes.data.success) setDetails(detailsRes.data.data);
      if (challengesRes.data.success) setChallenges(challengesRes.data.data || []);
    } catch (err) {
      console.error('Blad wczytywania premii:', err);
    } finally {
      setLoading(false);
    }
  }, [rok, miesiac, locationId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div style={{ padding: '0.75rem' }}>
        <div style={{ ...card, textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>Ladowanie danych premii...</div>
      </div>
    );
  }

  // Backend returns nested structure: { lokalizacje: [...] } or flat { sprzedaz_ogolem, premia_razem, ... }
  const bRaw = bonus || {};
  // If multi-location, grab first location; otherwise use flat data
  const b = bRaw.lokalizacje && bRaw.lokalizacje.length > 0 ? bRaw.lokalizacje[0] : bRaw;
  const d = details || {};

  const sprzedaz = Number(b.sprzedaz_ogolem || b.sprzedaz || 0);
  const bazowa = Number(b.premia_bazowa || 0);
  const produktowa = Number(b.premia_produktowa || 0);
  const razem = Number(b.premia_razem || b.razem || bazowa + produktowa);
  const nextProg = b.nastepny_prog || {};
  const nextThreshold = Number(nextProg.kwota || nextProg.brakuje || 0);
  const nextThresholdBonus = Number(nextProg.premia || 0);

  return (
    <div className="bonus-page-root" style={{ padding: '0.75rem', fontSize: '12px' }}>
      <style>{`
        @media (max-width: 600px) {
          .bonus-page-root .bonus-stat-grid > div { min-width: 0 !important; flex: 1 1 calc(50% - 8px) !important; }
          .bonus-page-root .admin-table-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .bonus-page-root .admin-table-wrap table { min-width: 500px; }
        }
      `}</style>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '0.5rem' }}>
        <h4 style={{ margin: 0, fontSize: '16px', color: 'var(--text)' }}>Moja premia</h4>
        <MonthSelector rok={rok} miesiac={miesiac} onChange={onMonthChange} />
      </div>

      {/* Stat cards */}
      <div className="bonus-stat-grid" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
        <div style={statCard('var(--info)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Sprzedaz</div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(sprzedaz)} zl</div>
        </div>
        <div style={statCard('var(--primary)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Premia bazowa</div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(bazowa)} zl</div>
        </div>
        <div style={statCard('var(--warning)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>Premia produktowa</div>
          <div style={{ fontSize: '18px', fontWeight: 700 }}>{fmt(produktowa)} zl</div>
        </div>
        <div style={statCard('var(--success)')}>
          <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>RAZEM</div>
          <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--primary)' }}>{fmt(razem)} zl</div>
        </div>
      </div>

      {/* Progress to next threshold */}
      {nextProg && nextProg.kwota > 0 && (
        <div style={{ ...card, padding: '10px 12px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px' }}>
            Do nastepnego progu ({fmt(nextProg.kwota)} zl → premia {fmt(nextProg.premia)} zl) — brakuje {fmt(nextProg.brakuje)} zl
          </div>
          <ProgressBar value={sprzedaz} max={nextProg.kwota} height={10} showLabel />
        </div>
      )}

      {/* Premium product sales */}
      <div style={card}>
        <h5 style={sectionTitle}>Sprzedaz produktow premiowych</h5>
        {(d.podsumowanie || d.produkty || []).length === 0 ? (
          <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
            Brak sprzedazy produktow premiowych w tym miesiacu.
          </div>
        ) : (
          <div className="admin-table-wrap">
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={th}>Produkt</th>
                <th style={{ ...th, textAlign: 'right' }}>Ilosc</th>
                <th style={{ ...th, textAlign: 'right' }}>Kwota/szt.</th>
                <th style={{ ...th, textAlign: 'right' }}>Premia</th>
              </tr>
            </thead>
            <tbody>
              {(d.podsumowanie || d.produkty || []).map((p, i) => (
                <tr key={i}>
                  <td style={td}>{p.nazwa}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{p.sprzedane ?? p.ilosc ?? p.ok}</td>
                  <td style={{ ...td, textAlign: 'right' }}>{fmt(p.kwota_za_sztuke ?? p.kwota_za_szt)}</td>
                  <td style={{ ...td, textAlign: 'right', fontWeight: 600 }}>{fmt(p.premia)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
      </div>

      {/* Challenges */}
      {challenges.length > 0 && (
        <div style={card}>
          <h5 style={sectionTitle}>Wyzwania</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {challenges.map((ch) => {
              const myProgress = ch.moj_postep || {};
              const celValue = ch.cel_wartosc ?? ch.cel ?? 0;
              const currentValue = myProgress.wartosc_aktualna || myProgress.wartosc || 0;
              const pct = celValue > 0 ? Math.min(100, (currentValue / celValue) * 100) : 0;
              return (
                <div key={ch.id} style={{
                  padding: '10px', borderRadius: '6px',
                  border: '1px solid var(--border)', backgroundColor: 'var(--bg)'
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600, fontSize: '12px' }}>{ch.nazwa}</span>
                    <span style={{ fontSize: '11px', color: 'var(--success)', fontWeight: 600 }}>{fmt(ch.nagroda)} zl</span>
                  </div>
                  {ch.opis && <div style={{ fontSize: '10px', color: 'var(--text-muted)', marginBottom: '4px' }}>{ch.opis}</div>}
                  <ProgressBar value={currentValue} max={celValue || 1} height={8} showLabel />
                  {pct >= 100 && (
                    <div style={{ fontSize: '10px', color: 'var(--success)', fontWeight: 600, marginTop: '2px' }}>
                      Wyzwanie ukonczone!
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* How it works button */}
      <div style={{ display: 'flex', justifyContent: 'center', marginTop: '12px' }}>
        <button className="btn" style={{ fontSize: '12px', color: 'var(--text-muted)' }}
          onClick={() => toast('Sekcja w przygotowaniu', { icon: 'i' })}>
          Jak to dziala?
        </button>
      </div>
    </div>
  );
}
